package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.file.PickedFile
import br.com.vvrefeicoes.portal.domain.model.UserDocument
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.UserDocumentRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.forms.MultiPartFormDataContent
import io.ktor.client.request.forms.formData
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteUserDocumentRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : UserDocumentRepository {
    override suspend fun load(userId: String): List<UserDocument> {
        val response = httpClient.get("$baseUrl/s3/users/$userId/docs") { authorize() }
        ensureSuccess(response)
        return response.body<List<DocumentDto>>().map { it.toDomain() }
    }

    override suspend fun upload(
        userId: String,
        file: PickedFile,
        source: String,
    ): UserDocument {
        val response =
            httpClient.post("$baseUrl/s3/users/$userId/docs") {
                authorize()
                setBody(
                    MultiPartFormDataContent(
                        formData {
                            append(
                                "file",
                                file.bytes,
                                Headers.build {
                                    append(HttpHeaders.ContentType, file.contentType)
                                    append(HttpHeaders.ContentDisposition, "filename=\"${file.name.replace("\"", "")}\"")
                                },
                            )
                            append("source", source)
                        },
                    ),
                )
            }
        ensureSuccess(response)
        return response.body<DocumentDto>().toDomain()
    }

    override suspend fun download(documentId: String): ByteArray {
        val response = httpClient.get("$baseUrl/s3/docs/$documentId/download") { authorize() }
        ensureSuccess(response)
        return response.body()
    }

    override suspend fun openUrl(documentId: String): String {
        val response = httpClient.get("$baseUrl/s3/docs/$documentId/url") { authorize() }
        ensureSuccess(response)
        return response.body<OpenUrlDto>().url
    }

    override suspend fun delete(documentId: String) {
        val response = httpClient.delete("$baseUrl/s3/docs/$documentId") { authorize() }
        ensureSuccess(response)
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    private suspend fun ensureSuccess(response: HttpResponse) {
        if (response.status.isSuccess()) return
        val message =
            runCatching {
                Json { ignoreUnknownKeys = true }
                    .decodeFromString<DocumentApiErrorDto>(response.bodyAsText())
                    .message
            }.getOrNull()
        error(message ?: "HTTP ${response.status.value}")
    }
}

@Serializable
private data class DocumentDto(
    val id: String = "",
    val originalFileName: String = "",
    val source: String = "",
    val contentType: String = "application/octet-stream",
    val sizeBytes: Long = 0,
)

private fun DocumentDto.toDomain() =
    UserDocument(
        id,
        originalFileName,
        source,
        contentType,
        sizeBytes,
    )

@Serializable
private data class DocumentApiErrorDto(
    val message: String? = null,
)

@Serializable
private data class OpenUrlDto(
    val url: String,
)
